import { useEffect, useState } from "react";
import { UserCog, Plus, Copy, Power } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";

export default function Assistants() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  const load = async () => setRows((await api.get("/assistants")).data);
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/assistants", form);
      setCreated(data);
      setForm({ name: "", email: "", phone: "" });
      toast.success("Asistente creado");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (id) => {
    await api.patch(`/assistants/${id}/toggle`);
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Asistentes</h1>
          <p className="mt-1 text-sm font-semibold text-slate-500">Crea cuentas con contraseña temporal.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setCreated(null); }}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-[#0284C7] font-bold hover:bg-[#0369a1]" data-testid="new-assistant-btn"><Plus className="mr-1.5 h-4 w-4" /> Nuevo asistente</Button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl bg-white">
            <DialogHeader><DialogTitle className="text-xl font-extrabold">Nuevo asistente</DialogTitle></DialogHeader>
            {created ? (
              <div className="space-y-4">
                <p className="text-sm font-semibold text-slate-500">Comparte estas credenciales. El asistente deberá cambiar la contraseña al primer inicio de sesión.</p>
                <div className="rounded-xl border border-[#E2E8F0] bg-slate-50 p-4">
                  <div className="text-sm font-bold text-slate-700">{created.email}</div>
                  <div className="mt-2 flex items-center justify-between rounded-lg bg-white px-3 py-2 border border-[#E2E8F0]">
                    <span className="font-mono text-sm font-bold text-[#0284C7]" data-testid="temp-password">{created.temp_password}</span>
                    <button onClick={() => { navigator.clipboard.writeText(created.temp_password); toast.success("Copiado"); }} data-testid="copy-password-btn"><Copy className="h-4 w-4 text-slate-400" /></button>
                  </div>
                </div>
                <Button onClick={() => { setOpen(false); setCreated(null); }} className="w-full rounded-full bg-[#0284C7] font-bold">Listo</Button>
              </div>
            ) : (
              <form onSubmit={create} className="space-y-4">
                <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Nombre</Label><Input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="mt-1.5 rounded-xl border-[#E2E8F0]" data-testid="assistant-name" /></div>
                <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Teléfono</Label><Input required value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className="mt-1.5 rounded-xl border-[#E2E8F0]" data-testid="assistant-phone" /></div>
                <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Correo</Label><Input required type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="mt-1.5 rounded-xl border-[#E2E8F0]" data-testid="assistant-email" /></div>
                <p className="text-xs font-semibold text-slate-400">La contraseña se genera automáticamente y el asistente deberá cambiarla en su primer inicio de sesión.</p>
                <Button type="submit" disabled={busy} className="w-full rounded-full bg-[#0284C7] font-bold hover:bg-[#0369a1]" data-testid="create-assistant-submit">{busy ? "Creando…" : "Crear asistente"}</Button>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-6 space-y-3">
        {rows.length === 0 && (
          <div className="rounded-2xl border border-dashed border-[#E2E8F0] bg-white p-10 text-center">
            <UserCog className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-3 text-sm font-bold text-slate-500">No has creado asistentes.</p>
          </div>
        )}
        {rows.map((a) => (
          <div key={a.user_id} className="flex items-center justify-between rounded-2xl border border-[#E2E8F0] bg-white p-5" data-testid={`assistant-${a.user_id}`}>
            <div>
              <div className="text-sm font-extrabold text-slate-900">{a.name}</div>
              <div className="text-xs font-bold text-slate-400">{a.email} {a.must_change_password && "· pendiente de activar clave"}</div>
            </div>
            <div className="flex items-center gap-3">
              <span className={`rounded-full border px-3 py-1 text-xs font-bold ${a.active ? "border-emerald-200 bg-emerald-50 text-emerald-600" : "border-slate-200 bg-slate-100 text-slate-400"}`}>{a.active ? "Activo" : "Inactivo"}</span>
              <button onClick={() => toggle(a.user_id)} className="rounded-full p-2 text-slate-400 hover:bg-slate-50" data-testid={`toggle-${a.user_id}`}><Power className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
